import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronLeft, Plus, Target, Smartphone, MapPin, Clock, Users, Trash2,
  CheckCircle, XCircle, Hourglass, ExternalLink, Image as ImageIcon,
  Video, FileText, Music,
} from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import {
  Button, Card, Spinner, EmptyState, Badge, SearchInput, PaginationBar,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Textarea, Select, SlidePanel, DeleteConfirmModal,
} from '../../../components/ui'
import { Thead, Tbody, Tr, Th, Td } from '../../../components/ui/Table'
import MediaPickerModal from '../../../components/MediaPickerModal'

interface Mission {
  id: number; title: string; description: string | null; action_type: string
  target_platforms: string | null; points: number; points_per_platform: number
  bonus_points: number; deadline: string | null; status: string
  category_name: string | null; category_color: string | null
  submissions_count: number; created_at: string
}

interface Submission {
  id: number; user_name: string; user_phone: string; platform: string | null
  post_url: string | null; photo_url: string | null; status: string
  points_earned: number; created_at: string
}

interface Category { id: number; name: string }

const typeIcons: Record<string, React.ElementType> = { image: ImageIcon, video: Video, audio: Music, document: FileText }
const typeLabels: Record<string, string> = { image: 'Imagem', video: 'Vídeo', audio: 'Áudio', document: 'Documento' }

const actionTypes = [
  { value: 'post_social', label: 'Postar nas redes', category: 'Digital', icon: Smartphone },
  { value: 'share_whatsapp', label: 'Compartilhar no WhatsApp', category: 'Digital', icon: Smartphone },
  { value: 'repost', label: 'Repostar post', category: 'Engajamento', icon: Smartphone },
  { value: 'comment', label: 'Comentar em post', category: 'Engajamento', icon: Smartphone },
  { value: 'engage', label: 'Repostar + Comentar', category: 'Engajamento', icon: Smartphone },
  { value: 'take_photo', label: 'Tirar foto', category: 'Presencial', icon: MapPin },
  { value: 'checkin', label: 'Check-in no local', category: 'Presencial', icon: MapPin },
  { value: 'sticker_car', label: 'Adesivo no carro', category: 'Presencial', icon: MapPin },
  { value: 'sticker_house', label: 'Adesivo na fachada', category: 'Presencial', icon: MapPin },
  { value: 'door_to_door', label: 'Visita porta a porta', category: 'Presencial', icon: MapPin },
  { value: 'attend_event', label: 'Participar de evento', category: 'Presencial', icon: MapPin },
]

const linkActionTypes = ['repost', 'comment', 'engage']

const platforms = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'kwai', label: 'Kwai' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
]

const statusConfig: Record<string, { label: string; variant: string }> = {
  active: { label: 'Ativa', variant: 'success' },
  completed: { label: 'Concluída', variant: 'info' },
  expired: { label: 'Expirada', variant: 'gray' },
  cancelled: { label: 'Cancelada', variant: 'error' },
}

export default function CommunityMissionsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [missions, setMissions] = useState<Mission[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 20, last_page: 1 })

  // Modal criar
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: '', description: '', action_type: 'post_social', target_platforms: ['instagram', 'tiktok', 'kwai'],
    platform_links: {} as Record<string, string>,
    points: 60, points_per_platform: 20, bonus_points: 0, deadline: '', category_id: '',
    location_address: '', location_lat: '', location_lng: '', location_radius: '',
    media_ids: [] as number[],
  })
  const [availableMedia, setAvailableMedia] = useState<{ id: number; title: string; type: string; file_url: string }[]>([])
  const [mediaModalOpen, setMediaModalOpen] = useState(false)

  // Delete
  const [deleteMission, setDeleteMission] = useState<Mission | null>(null)

  // Submissions panel
  const [selectedMission, setSelectedMission] = useState<Mission | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loadingSubs, setLoadingSubs] = useState(false)
  const [reviewing, setReviewing] = useState<number | null>(null)

  const fetchMissions = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { search, page, per_page: perPage, status: statusFilter }
      const res = await axios.get(buildApiUrl('/v1/community/missions'), { params })
      setMissions(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch {} finally { setLoading(false) }
  }, [search, page, perPage, statusFilter])

  useEffect(() => { fetchMissions() }, [fetchMissions])
  useEffect(() => { setPage(1) }, [search, statusFilter, perPage])
  useEffect(() => {
    axios.get(buildApiUrl('/v1/community/categories')).then(res => setCategories(res.data.data)).catch(() => {})
  }, [])

  function openCreate() {
    setForm({
      title: '', description: '', action_type: 'post_social', target_platforms: [],
      platform_links: {},
      points: 60, points_per_platform: 20, bonus_points: 0, deadline: '', category_id: '',
      location_address: '', location_lat: '', location_lng: '', location_radius: '',
      media_ids: [],
    })
    axios.get(buildApiUrl('/v1/community/media'), { params: { per_page: 100 } })
      .then(res => setAvailableMedia(res.data.data.map((m: any) => ({ id: m.id, title: m.title, type: m.type, file_url: m.file_url }))))
      .catch(() => {})
    setModalOpen(true)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim()) return
    setSaving(true)
    try {
      const payload: any = {
        title: form.title, description: form.description, action_type: form.action_type,
        points: form.points, points_per_platform: form.points_per_platform || 0,
        bonus_points: form.bonus_points || 0,
      }
      if (form.media_ids.length > 0) payload.media_ids = form.media_ids
      if (form.deadline) payload.deadline = form.deadline
      if (form.category_id) payload.category_id = parseInt(form.category_id)
      if ((form.action_type === 'post_social' || linkActionTypes.includes(form.action_type)) && form.target_platforms.length > 0) {
        payload.target_platforms = JSON.stringify(form.target_platforms)
      }
      if (linkActionTypes.includes(form.action_type) && Object.keys(form.platform_links).length > 0) {
        payload.platform_links = JSON.stringify(form.platform_links)
      }
      if (['checkin', 'attend_event'].includes(form.action_type)) {
        if (form.location_address) payload.location_address = form.location_address
        if (form.location_lat) payload.location_lat = parseFloat(form.location_lat)
        if (form.location_lng) payload.location_lng = parseFloat(form.location_lng)
        if (form.location_radius) payload.location_radius = parseInt(form.location_radius)
      }
      await axios.post(buildApiUrl('/v1/community/missions'), payload)
      toast('success', 'Missão criada')
      setModalOpen(false)
      fetchMissions()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao criar')
    } finally { setSaving(false) }
  }

  function refreshMedia() {
    axios.get(buildApiUrl('/v1/community/media'), { params: { per_page: 100 } })
      .then(res => setAvailableMedia(res.data.data.map((m: any) => ({ id: m.id, title: m.title, type: m.type, file_url: m.file_url }))))
      .catch(() => {})
  }

  async function handleDeleteMission() {
    if (!deleteMission) return
    try {
      await axios.delete(buildApiUrl(`/v1/community/missions/${deleteMission.id}`))
      toast('success', 'Missão removida')
      setDeleteMission(null)
      fetchMissions()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao remover')
    }
  }

  async function openSubmissions(mission: Mission) {
    setSelectedMission(mission)
    setLoadingSubs(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/community/missions/${mission.id}/submissions`))
      setSubmissions(res.data.data)
    } catch {} finally { setLoadingSubs(false) }
  }

  async function handleReview(subId: number, status: 'approved' | 'rejected') {
    setReviewing(subId)
    try {
      await axios.put(buildApiUrl(`/v1/community/submissions/${subId}/review`), { status })
      toast('success', status === 'approved' ? 'Aprovada!' : 'Rejeitada')
      if (selectedMission) openSubmissions(selectedMission)
      fetchMissions()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro')
    } finally { setReviewing(null) }
  }

  const isDigital = form.action_type === 'post_social' || form.action_type === 'share_whatsapp'
  const needsLocation = form.action_type === 'checkin' || form.action_type === 'attend_event'

  const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR')
  const formatDateTime = (d: string) => new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/community/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Missões</h1>
            <p className="text-gray-500 mt-1">Crie e gerencie missões da comunidade</p>
          </div>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Missão
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <SearchInput placeholder="Buscar missão..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1">
          {['all', 'active', 'completed', 'expired'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${statusFilter === s ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s === 'all' ? 'Todas' : statusConfig[s]?.label || s}
            </button>
          ))}
        </div>
      </div>

      {/* Tabela */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <Thead>
              <Tr>
                <Th>Missão</Th>
                <Th>Tipo</Th>
                <Th>Pontos</Th>
                <Th>Prazo</Th>
                <Th>Entregas</Th>
                <Th>Status</Th>
                <Th className="text-right">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Tr key={i}>{Array.from({ length: 6 }).map((_, j) => <Td key={j}><div className="h-4 bg-gray-100 rounded animate-pulse" /></Td>)}</Tr>
                ))
              ) : missions.length === 0 ? (
                <Tr><Td colSpan={6} className="text-center text-gray-500 py-8">Nenhuma missão encontrada</Td></Tr>
              ) : missions.map(m => {
                const at = actionTypes.find(a => a.value === m.action_type)
                const sc = statusConfig[m.status] || statusConfig.active
                const AtIcon = at?.icon || Target
                return (
                  <Tr key={m.id} onClick={() => openSubmissions(m)} className="cursor-pointer hover:bg-gray-50">
                    <Td>
                      <p className="text-sm font-medium text-gray-800">{m.title}</p>
                      {m.category_name && <p className="text-xs text-gray-400">{m.category_name}</p>}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <AtIcon className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs text-gray-600">{at?.label || m.action_type}</span>
                      </div>
                    </Td>
                    <Td><span className="text-sm font-semibold text-brand-600">+{m.points}</span></Td>
                    <Td><span className="text-sm text-gray-500">{m.deadline ? formatDate(m.deadline) : '—'}</span></Td>
                    <Td>
                      <span className="text-sm text-gray-600">{m.submissions_count ?? 0}</span>
                    </Td>
                    <Td><Badge variant={sc.variant as any} size="sm">{sc.label}</Badge></Td>
                    <Td className="text-right">
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteMission(m) }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-error-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </table>
        </div>
        <PaginationBar page={page} perPage={perPage} total={meta.total} lastPage={meta.last_page} onPageChange={setPage} onPerPageChange={n => { setPerPage(n); setPage(1) }} />
      </Card>

      {/* Modal criar missão */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setModalOpen(false)}>Nova Missão</ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalBody className="space-y-4 max-h-[70vh] overflow-y-auto">
            <Input label="Título *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Compartilhar corte sobre educação" required />
            <Textarea label="Descrição / Instruções" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="O que o membro precisa fazer nesta missão" rows={3} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select label="Tipo de ação *" value={form.action_type} onChange={e => setForm(f => ({ ...f, action_type: e.target.value }))}
                options={actionTypes.map(a => ({ value: a.value, label: `${a.category} — ${a.label}` }))} />
              {categories.length > 0 && (
                <Select label="Categoria" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                  options={[{ value: '', label: 'Sem categoria' }, ...categories.map(c => ({ value: String(c.id), label: c.name }))]} />
              )}
            </div>

            {/* Texto/legenda pronta pra copiar */}
            <div>
              <Textarea
                label="Texto pra compartilhar"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={"Ex:\nA educação é a base de tudo! 📚\n\n✅ Mais creches\n✅ Escola em tempo integral\n\n#Educação #VotoConsciente"}
                rows={5}
                hint="O membro toca pra copiar e colar nas redes sociais. Use hashtags e emojis."
              />
            </div>

            {/* Mídias vinculadas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Mídias</label>
                <button type="button" onClick={() => setMediaModalOpen(true)}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" />
                  Selecionar da biblioteca
                </button>
              </div>

              {/* Mídias selecionadas */}
              {form.media_ids.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                  Nenhuma mídia selecionada
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {form.media_ids.map(id => {
                    const m = availableMedia.find(x => x.id === id)
                    if (!m) return null
                    const TypeIcon = typeIcons[m.type] || FileText
                    return (
                      <div key={id} className="flex items-center gap-2 px-2 py-1.5 bg-brand-25 border border-brand-200 rounded-lg">
                        {m.type === 'image' ? (
                          <img src={m.file_url} alt="" className="w-7 h-7 rounded object-cover" />
                        ) : (
                          <TypeIcon className="w-4 h-4 text-brand-500" />
                        )}
                        <span className="text-xs font-medium text-brand-700 max-w-[120px] truncate">{m.title}</span>
                        <button type="button" onClick={() => setForm(f => ({ ...f, media_ids: f.media_ids.filter(x => x !== id) }))}
                          className="text-brand-400 hover:text-error-500 transition-colors">
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Plataformas (post_social) */}
            {form.action_type === 'post_social' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plataformas *</label>
                <div className="flex flex-wrap gap-2">
                  {platforms.map(p => (
                    <label key={p.value} className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${form.target_platforms.includes(p.value) ? 'border-brand-400 bg-brand-25 text-brand-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <input type="checkbox" checked={form.target_platforms.includes(p.value)}
                        onChange={e => setForm(f => ({ ...f, target_platforms: e.target.checked ? [...f.target_platforms, p.value] : f.target_platforms.filter(x => x !== p.value) }))}
                        className="w-4 h-4 rounded border-gray-300 text-brand-500" />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Plataformas + Links (repost/comment/engage) */}
            {linkActionTypes.includes(form.action_type) && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Plataformas *</label>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map(p => (
                      <label key={p.value} className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${form.target_platforms.includes(p.value) ? 'border-brand-400 bg-brand-25 text-brand-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        <input type="checkbox" checked={form.target_platforms.includes(p.value)}
                          onChange={e => {
                            const checked = e.target.checked
                            setForm(f => ({
                              ...f,
                              target_platforms: checked ? [...f.target_platforms, p.value] : f.target_platforms.filter(x => x !== p.value),
                              platform_links: checked ? f.platform_links : Object.fromEntries(Object.entries(f.platform_links).filter(([k]) => k !== p.value)),
                            }))
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-brand-500" />
                        <span className="text-sm">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {form.target_platforms.length > 0 && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Links dos posts *</label>
                    {form.target_platforms.map(p => {
                      const platformLabel = platforms.find(x => x.value === p)?.label || p
                      return (
                        <div key={p}>
                          <label className="block text-xs text-gray-500 mb-1">{platformLabel}</label>
                          <input
                            type="url"
                            value={form.platform_links[p] || ''}
                            onChange={e => setForm(f => ({ ...f, platform_links: { ...f.platform_links, [p]: e.target.value } }))}
                            placeholder={`https://www.${p}.com/...`}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                      )
                    })}
                  </div>
                )}

                {(form.action_type === 'comment' || form.action_type === 'engage') && (
                  <p className="text-xs text-gray-500">💡 Use a descrição da missão pra sugerir o texto do comentário.</p>
                )}
              </div>
            )}

            {/* Localização (checkin/attend_event) */}
            {needsLocation && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Endereço" value={form.location_address} onChange={e => setForm(f => ({ ...f, location_address: e.target.value }))} placeholder="Praça Central, 123" className="sm:col-span-2" />
                <Input label="Latitude" value={form.location_lat} onChange={e => setForm(f => ({ ...f, location_lat: e.target.value }))} placeholder="-9.6498" />
                <Input label="Longitude" value={form.location_lng} onChange={e => setForm(f => ({ ...f, location_lng: e.target.value }))} placeholder="-35.7089" />
                <Input label="Raio (metros)" value={form.location_radius} onChange={e => setForm(f => ({ ...f, location_radius: e.target.value }))} placeholder="200" />
              </div>
            )}

            {/* Pontos */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input label="Pontos totais *" type="number" value={String(form.points)} onChange={e => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} />
              {form.action_type === 'post_social' && (
                <Input label="Pontos por rede" type="number" value={String(form.points_per_platform)} onChange={e => setForm(f => ({ ...f, points_per_platform: parseInt(e.target.value) || 0 }))} />
              )}
              <Input label="Bônus" type="number" value={String(form.bonus_points)} onChange={e => setForm(f => ({ ...f, bonus_points: parseInt(e.target.value) || 0 }))} />
            </div>

            <Input label="Prazo" type="datetime-local" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}><Plus className="w-4 h-4 mr-2" />Criar Missão</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Painel de submissions */}
      <SlidePanel
        open={!!selectedMission}
        onClose={() => setSelectedMission(null)}
        title={selectedMission?.title || ''}
        subtitle={`${submissions.length} entregas`}
      >
        {loadingSubs ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : submissions.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">Nenhuma entrega ainda</p>
        ) : (
          <div className="space-y-3">
            {submissions.map(sub => (
              <div key={sub.id} className="p-3 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{sub.user_name}</p>
                    <p className="text-xs text-gray-400">{sub.user_phone} · {formatDateTime(sub.created_at)}</p>
                  </div>
                  {sub.status === 'approved' && <Badge variant="success" size="sm"><CheckCircle className="w-3 h-3 mr-1" />Aprovada</Badge>}
                  {sub.status === 'rejected' && <Badge variant="error" size="sm"><XCircle className="w-3 h-3 mr-1" />Rejeitada</Badge>}
                  {sub.status === 'pending' && <Badge variant="warning" size="sm"><Hourglass className="w-3 h-3 mr-1" />Pendente</Badge>}
                </div>

                {sub.platform && <Badge variant="gray" size="sm" className="mb-2">{sub.platform}</Badge>}
                {sub.post_url && (
                  <a href={sub.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-600 hover:underline flex items-center gap-1 mb-2">
                    <ExternalLink className="w-3 h-3" />Ver post
                  </a>
                )}
                {sub.photo_url && (
                  <img src={sub.photo_url} alt="Prova" className="w-full h-32 object-cover rounded-lg mb-2" />
                )}

                {sub.status === 'pending' && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => handleReview(sub.id, 'approved')} loading={reviewing === sub.id}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />Aprovar
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleReview(sub.id, 'rejected')} loading={reviewing === sub.id}>
                      <XCircle className="w-3.5 h-3.5 mr-1" />Rejeitar
                    </Button>
                  </div>
                )}

                {sub.status === 'approved' && sub.points_earned > 0 && (
                  <p className="text-xs text-success-600 font-medium mt-1">+{sub.points_earned} pts</p>
                )}
              </div>
            ))}
          </div>
        )}
      </SlidePanel>

      <DeleteConfirmModal
        open={!!deleteMission}
        onClose={() => setDeleteMission(null)}
        onConfirm={handleDeleteMission}
        title="Remover missão"
        message={`Tem certeza que deseja remover a missão "${deleteMission?.title}"? As submissões serão mantidas.`}
      />

      {/* Modal biblioteca de mídias */}
      <MediaPickerModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        selectedIds={form.media_ids}
        onConfirm={(ids) => {
          setForm(f => ({ ...f, media_ids: ids }))
          refreshMedia()
        }}
        multiple
      />
    </div>
  )
}
